import React, {useCallback, useState} from 'react';

import {useTemplateSettings} from 'src/modules/profile/account/create-user/steps/users-settings/components/imports-settings';

import Button from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {Stepper, StepperItem} from '@pb/kit-component-stepper';

import {
    MIGRATION_STEPS, STEPS,
    downloadMigrationCsv, hasMigrationResult, useMigrationPreview,
} from '../../../migration-stepper';
import {migrationStepperStepper} from '../../../migration-stepper/migration-stepper.module.styl';

import {ConfigureStep, ConfirmStep, MigrationResult} from './steps';

import type {AccountShortInfo} from 'src/openapi/admin/models/AccountShortInfo';
import type {UserSearchesReplaceResponse} from 'src/openapi/admin/models/UserSearchesReplaceResponse';
import type {UserShortInfo} from 'src/openapi/admin/models/UserShortInfo';

export type ChangeAccountSubmitParams = {
    accountId: number,
    selectedTemplateUserId: number | null,
    savedSearches: boolean,
    emailAlerts: boolean,
    templates: boolean,
};
type Props = {
    userId: number,
    userName: string,
    onClose: () => void,
    onSubmitHandler: (params: ChangeAccountSubmitParams) => Promise<boolean>,
    shown: boolean,
    busy?: boolean,
    onFieldChange?: (parameterName: string) => void,
    migrationResult?: UserSearchesReplaceResponse | null,
};

export const ChangeAccountView = (props: Props) => {
    const {
        userId,
        userName,
        onClose,
        onSubmitHandler,
        shown,
        busy = false,
        onFieldChange,
        migrationResult,
    } = props;

    const [currentStep, setCurrentStep] = useState(
        MIGRATION_STEPS.CONFIGURE,
    );
    const [acknowledged, setAcknowledged] = useState(false);
    const [account, setAccount] = useState<
        AccountShortInfo | undefined
    >();
    const [selectedTemplateUser, setSelectedTemplateUser] = useState<
        UserShortInfo | undefined
    >();

    const {
        selectedTemplateUserId,
        setSelectedTemplateUserId,
        savedSearches,
        setSavedSearches,
        templates,
        setTemplates,
        emailAlerts,
        setEmailAlerts,
    } = useTemplateSettings({userId, defaultValue: false});

    const isConfirmStep = currentStep === MIGRATION_STEPS.CONFIRM;
    const isConfigure = currentStep === MIGRATION_STEPS.CONFIGURE;
    const isMigrate = currentStep === MIGRATION_STEPS.MIGRATE;

    const {preview, isLoading: previewLoading} = useMigrationPreview({
        userId,
        templateUserId: selectedTemplateUserId ?? userId,
        replaceSearches: savedSearches,
        replaceAlerts: emailAlerts,
        replaceLayouts: templates,
        autoload: isConfirmStep,
    });

    const onResetViewSettings = useCallback(() => {
        setAccount(undefined);
        setSelectedTemplateUserId(null);
        setSelectedTemplateUser(undefined);
        setCurrentStep(MIGRATION_STEPS.CONFIGURE);
        setAcknowledged(false);
    }, [setSelectedTemplateUserId]);

    const onCloseHandler = useCallback(() => {
        onClose();
        onResetViewSettings();
    }, [onClose, onResetViewSettings]);

    const onSetSelectedTemplateUser = useCallback(
        (templateUser: UserShortInfo | undefined) => {
            onFieldChange?.('Import alerts & layouts from user');
            setSelectedTemplateUser(templateUser);
            setSelectedTemplateUserId(
                templateUser?.userId ?? null,
            );
        },
        [setSelectedTemplateUserId, onFieldChange],
    );

    const onSelectAccount = useCallback(
        (newAccount: AccountShortInfo | undefined) => {
            onFieldChange?.('Select Account');
            setAccount(newAccount);

            if (!newAccount) {
                setSelectedTemplateUser(undefined);
                setSelectedTemplateUserId(null);
            }
        },
        [setSelectedTemplateUserId, onFieldChange],
    );

    const onContinue = useCallback(() => {
        setCurrentStep(MIGRATION_STEPS.CONFIRM);
    }, []);

    const onBack = useCallback(() => {
        setAcknowledged(false);
        setCurrentStep(MIGRATION_STEPS.CONFIGURE);
    }, []);

    const onSubmit = useCallback(async () => {
        const accountId = account?.accountId;
        if (accountId) {
            const success = await onSubmitHandler({
                accountId,
                selectedTemplateUserId,
                savedSearches,
                emailAlerts,
                templates,
            });

            if (success) {
                setCurrentStep(MIGRATION_STEPS.MIGRATE);
            }
        }
    }, [
        account?.accountId,
        selectedTemplateUserId,
        savedSearches,
        emailAlerts,
        templates,
        onSubmitHandler,
    ]);

    const onDownloadReport = useCallback(() => {
        if (migrationResult) {
            downloadMigrationCsv(
                migrationResult,
                'change_account_report.csv',
            );
        }
    }, [migrationResult]);

    let dialogWidth = 550;
    if (!isConfigure) {
        dialogWidth = 850;
    }

    return (
        <Dialog
            shown={shown}
            setShown={onCloseHandler}
            wrapperProps={{style: {width: dialogWidth}}}>
            <DialogHeader onClose={onCloseHandler}>
                Change user's account
            </DialogHeader>
            <DialogMain>
                <div className={migrationStepperStepper}>
                    <Stepper activeStep={currentStep}>
                        {STEPS.map(step => (
                            <StepperItem
                                key={step.step}
                                caption={step.caption}
                            />
                        ))}
                    </Stepper>
                </div>

                {isConfigure ? (
                    <ConfigureStep
                        account={account}
                        selectedTemplateUser={selectedTemplateUser}
                        selectedTemplateUserId={selectedTemplateUserId}
                        savedSearches={savedSearches}
                        emailAlerts={emailAlerts}
                        templates={templates}
                        onSelectAccount={onSelectAccount}
                        onSetSelectedTemplateUser={
                            onSetSelectedTemplateUser
                        }
                        setSavedSearches={setSavedSearches}
                        setTemplates={setTemplates}
                        setEmailAlerts={setEmailAlerts}
                        onFieldChange={onFieldChange}
                    />
                ) : null}

                {isConfirmStep ? (
                    <ConfirmStep
                        preview={preview}
                        previewLoading={previewLoading}
                        acknowledged={acknowledged}
                        onAcknowledgedChange={setAcknowledged}
                        hasTemplateUser={selectedTemplateUserId !== null}
                        hasContentSelected={
                            savedSearches || emailAlerts || templates
                        }
                        currentUserContent={
                            preview?.currentUserState ?? null
                        }
                    />
                ) : null}

                {isMigrate ? (
                    <MigrationResult
                        migrationResult={migrationResult}
                        userName={userName}
                        accountName={account?.accountName ?? ''}
                        hasTemplateUser={selectedTemplateUserId !== null}
                        onDownload={onDownloadReport}
                        downloadDisabled={!hasMigrationResult(migrationResult)}
                    />
                ) : null}
            </DialogMain>
            <DialogFooter>
                {isConfigure ? (
                    <Group>
                        <Button
                            variant="Type.PRIMARY"
                            disabled={!account}
                            onClick={onContinue}>
                            Continue
                        </Button>
                        <Button
                            variant="Type.SECONDARY"
                            onClick={onCloseHandler}>
                            Cancel
                        </Button>
                    </Group>
                ) : null}

                {isConfirmStep ? (
                    <Group>
                        <Button
                            variant="Type.SECONDARY"
                            onClick={onBack}>
                            Back
                        </Button>
                        <Button
                            variant="Type.PRIMARY"
                            disabled={!acknowledged}
                            busy={busy}
                            onClick={onSubmit}>
                            Start Migration
                        </Button>
                    </Group>
                ) : null}

                {isMigrate ? (
                    <Button
                        variant="Type.PRIMARY"
                        onClick={onCloseHandler}>
                        Done
                    </Button>
                ) : null}
            </DialogFooter>
        </Dialog>
    );
};
